import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { QueryParams } from 'src/app/modules/core/store/models/query.model';
import { TranslateService } from '@ngx-translate/core';
import { current, produce } from 'immer';
import { Folder, FolderVisibility, Layer, LayerState, LayerTypes, SectionType } from '../cop/models';
import * as copActions from '../cop/cop.actions';
import * as copSelectors from '../cop/cop.selectors';
import * as lsdSelectors from '../lsd/lsd.selectors';
import Environment from '@env';
import { layerTypesFilteredForSection } from './models/layer-configuration.model';
import { FolderConfiguration } from './models/map-configuration.model';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import { FolderUtils } from '../../services/map/folder-utils';
import { MapLayerSummary, MapLayerSummaryByStatus, MapLayerSummaryStatusCounts } from './models/map-layer-summary.model';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { SystemFieldNames } from 'src/app/modules/core/store/models';
import { WorkflowUtils } from '../../services/map/workflow-utils';
import { MapStateService } from '../../services';
import { ImageTileLayer } from '../cop/models/map-state.model';

@Injectable()
export class CopLeftDrawerEffects {
  constructor(
    private datasetApiService: DatasetApiService,
    private actions$: Actions,
    private store$: Store<any>,
    private translateService: TranslateService,
    private mapStateService: MapStateService
  ) {}

  newFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.newFolder),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { parentFolder } = action;

        const sortOrder = parentFolder.folders?.length + 1;

        const folder: Folder = {
          uId: uuidv4(),
          parentUId: parentFolder.uId,
          name: action.sectionType
            ? this.translateService.instant('MAP.DEFAULT-SECTION-NAME')
            : this.translateService.instant('MAP.DEFAULT-FOLDER-NAME'),
          sectionType: action.sectionType || SectionType.None,
          sortOrder,
          folders: [],
          layers: []
        };

        // add the folder to the map
        const updatedMapState = produce(mapState, draft => {
          const rootFolder = draft.map.folders[0];
          this.tryAddFolderToFolder(parentFolder.uId, rootFolder, folder);
          draft.foldersSet[parentFolder.uId].folders.push(folder);
          this.mapStateService.updateMapStateSetsFromMap(draft);
        });

        return [
          copActions.expandFolderToStore({ folder: parentFolder }),
          copActions.storeNewFolderAdditionToStore({ mapState: updatedMapState, folder })
        ];
      })
    )
  );

  updateDraftFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.updateDraftFolder),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { folder } = action;

        const updatedMapState = produce(mapState, draft => {
          this.updateFolderInMap(draft.map.folders[0], folder);
          this.mapStateService.updateMapStateSetsFromMap(draft);
        });

        return [
          copActions.calculateWorkflowStatusSummary({
            summaryItems: WorkflowUtils.getWorkflowStatusSummaryModelFromMap(updatedMapState.map)
          }),
          copActions.setDraftMapToStore({ mapState: updatedMapState, hideFolderEdit: action.hideFolderEdit })
        ];
      })
    )
  );

  saveFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.saveFolder),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { folder } = action;

        const updatedMapState = produce(mapState, draft => {
          this.updateFolderInMap(draft.map.folders[0], folder);
          this.mapStateService.updateMapStateSetsFromMap(draft);
        });

        return [copActions.storeSavedFolderToStore({ mapState: updatedMapState })];
      })
    )
  );

  deleteSelectedFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.deleteSelectedFolder),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectSelectedFolderForDelete)
      ),
      switchMap(([action, mapState, folder]) => {
        const containedLayers = this.getAllLayersInFolder(folder);

        const updatedMapState = produce(mapState, draft => {
          this.removeFolderInMap(draft.map.folders[0], folder);
          this.mapStateService.updateMapStateSetsFromMap(draft);
        });

        const thenActions = [];
        thenActions.push(copActions.storeDeleteOfSelectedFolderToStore({ mapState: updatedMapState }));

        return [copActions.removeSelectedMapDrawingAndDocumentsForFolder({ folderUId: folder.uId, then: thenActions })];
      })
    )
  );

  expandCollapseFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.expandCollapseFolder),
      withLatestFrom(this.store$.select(copSelectors.selectFolderExpanded)),
      switchMap(([action, folderExpanded]) => {
        const { folder } = action;

        if (!!folderExpanded && folderExpanded[folder.uId] === true) {
          return [copActions.collapseFolderToStore({ folder })];
        } else {
          return [copActions.expandFolderToStore({ folder })];
        }
      })
    )
  );

  newLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.newLayer),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { parentFolder } = action;

        const section = Object.values(mapState.foldersSet).find(
          f =>
            f.sectionType > 0 &&
            ((f.uId === parentFolder.uId ? true : undefined) ||
              !!f.folders.find(vl => vl.uId === parentFolder.uId) ||
              !!f.folders.find(vf => vf.folders.find(vl => vl.uId === parentFolder.uId)))
        );

        if (!section && mapState.map.version === 1) {
          return [];
        }
        const layerTypes = layerTypesFilteredForSection(section);

        if (layerTypes.length === 0) {
          if (mapState.map.version === 1) {
            return [];
          }
        }

        const sortOrder = parentFolder.layers?.length + 1;

        const layer = {
          uId: uuidv4(),
          folderUId: parentFolder.uId,
          name: '',
          sortOrder,
          isActive: true,
          type: layerTypes[0]?.value || LayerTypes.Other,
          color: '#03A9F4',
          iconPath: null,
          datasetUId: null,
          displayOptions: {
            shapeOpacity: 0.4
          },
          foreignDatasetDefinition: null,
          rule: null
        } as Layer;

        // add the layer to the map
        const updatedMapState = produce(mapState, draft => {
          const rootFolder = draft.map.folders[0];
          this.tryAddLayerToFolder(parentFolder.uId, rootFolder, layer);
          this.mapStateService.updateMapStateSetsFromMap(draft);
        });

        return [
          copActions.expandFolderToStore({ folder: parentFolder }),
          copActions.storeNewLayerAddedToStore({ mapState: updatedMapState, layer })
        ];
      })
    )
  );

  cancelEditLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.cancelEditLayer),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectSelectedLayer),
        this.store$.select(copSelectors.selectIsLayerNew)
      ),
      switchMap(([action, mapState, layer, isNew]) => {
        if (!isNew) {
          return [copActions.storeCancelEditLayerToStore()];
        }

        const updatedMapState = produce(mapState, draft => {
          this.removeLayerInMap(draft.map.folders[0], layer);
          this.mapStateService.updateMapStateSetsFromMap(draft);
        });

        return [copActions.storeNewLayerCanceledToStore({ mapState: updatedMapState })];
      })
    )
  );

  saveWeatherLayers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.saveWeatherLayers),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { parentFolder } = action;

        let defaultLayerName;
        this.translateService.get('MAP.DEFAULT-LAYER-NAME').subscribe(res => (defaultLayerName = res));

        const sortOrder = parentFolder.layers?.length + 1;

        const layers = [
          {
            uId: uuidv4(),
            name: 'Aeris Weather',
            sortOrder,
            isActive: true,
            type: mapState.map.version === 0 ? LayerTypes.Tile : LayerTypes.WeatherImagery,
            color: '#03A9F4',
            iconPath: null,
            datasetUId: null,
            displayOptions: {},
            foreignDatasetDefinition: {
              name: 'aeris-all',
              urlTemplate: 'https://maps.aerisapi.com/{client_id}_{client_key}/{options}/{z}/{x}/{y}/current.png',
              useSystemClientId: 'aeris',
              templateGroup: 'aeris-weather',
              opacity: 1,
              options: [
                { name: 'Radar', value: 'radar', enabled: false },
                { name: 'Stormcells', value: 'stormcells', enabled: false },
                { name: 'Tropical Cyclones', value: 'tropical-cyclones', enabled: false },
                { name: 'Satellite Visible', value: 'satellite-visible', enabled: false },
                { name: 'Satellite', value: 'satellite', enabled: false },
                { name: 'Estimated Snow Depth', value: 'snow-depth', enabled: false },
                { name: 'Forecast Snow Depth', value: 'fsnow-depth', enabled: false },
                { name: 'Forecast Ice Accumulation', value: 'fice-accum', enabled: false },
                { name: 'Forecast Snow Accumulation', value: 'fqsf-accum', enabled: false }
              ],
              refreshInterval: 180000
            },
            rule: null
          } as Layer
        ];

        // add the layer to the map
        const updatedMapState = produce(mapState, draft => {
          const rootFolder = draft.map.folders[0];
          layers.forEach(layer => this.tryAddLayerToFolder(parentFolder.uId, rootFolder, layer));
          this.mapStateService.updateMapStateSetsFromMap(draft);
        });

        return [copActions.expandFolderToStore({ folder: parentFolder }), copActions.storeSavedLayerToStore({ mapState: updatedMapState })];
      })
    )
  );

  saveLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.saveLayer),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { layer } = action;

        const updatedMapState = produce(mapState, draft => {
          this.updateLayerInMap(draft.map.folders[0], layer);
          this.mapStateService.updateMapStateSetsFromMap(draft);
          if (!!action.refreshLayersOnly) {
            draft.layersSet[layer.uId] = layer;
          }
        });

        if (!!action.refreshLayersOnly) {
          return [
            copActions.loadMapWithStateCompleteToStore({ mapWithState: updatedMapState }),
            copActions.saveSelectedMap({
              mapName: null,
              cancelEdit: false,
              refreshLayersOnly: action.refreshLayersOnly,
              refreshLabelsOnly: action.refreshLabelsOnly
            })
          ];
        } else {
          const thenActions = [];
          thenActions.push(copActions.storeSavedLayerToStore({ mapState: updatedMapState }));
          thenActions.push(
            copActions.calculateWorkflowStatusSummary({
              summaryItems: WorkflowUtils.getWorkflowStatusSummaryModelFromMap(updatedMapState.map)
            })
          );
          thenActions.push(copActions.loadDocumentsForMapLayer({ layerUId: layer.uId }));

          return [copActions.removeSelectedMapDrawingAndDocumentsForLayer({ layerUId: layer.uId, then: thenActions })];
        }
      })
    )
  );

  deleteSelectedLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.deleteSelectedLayer),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectSelectedLayerForDelete)
      ),
      switchMap(([action, mapState, layer]) => {
        const updatedMapState = produce(mapState, draft => {
          this.removeLayerInMap(draft.map.folders[0], layer);
          this.mapStateService.updateMapStateSetsFromMap(draft);
        });

        const thenActions = [];
        thenActions.push(copActions.storeDeleteOfSelectedLayerToStore({ mapState: updatedMapState }));
        thenActions.push(
          copActions.calculateWorkflowStatusSummary({
            summaryItems: WorkflowUtils.getWorkflowStatusSummaryModelFromMap(updatedMapState.map)
          })
        );

        return [copActions.removeSelectedMapDrawingAndDocumentsForLayer({ layerUId: layer.uId, then: thenActions })];
      })
    )
  );

  loadDatasetsForSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.loadDatasetsForSelect),
      switchMap(action => {
        const queryParams: QueryParams = {
          skip: 0,
          take: 500,
          sortField: 'Name',
          sortDirection: 'Ascending'
        };

        return this.datasetApiService.getDatasets(queryParams, true, true).pipe(
          map(res => copActions.storeLoadedDatasetsForSelectionToStore({ results: res })),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  hideShowLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.hideShowLayer),
      withLatestFrom(this.store$.select(copSelectors.selectLayerVisibility)),
      switchMap(([action, layerVisibility]) => {
        const { layer } = action;

        const layerIsVisibleAfterChange =
          layer.type === LayerTypes.WeatherImagery && layerVisibility[layer.uId] === undefined
            ? true
            : layerVisibility[layer.uId] === false;

        return [
          copActions.setLayerVisibilitiesToStore({ layers: [layer], showLayer: layerIsVisibleAfterChange }),
          copActions.updateFolderPartialVisibilityState({ folderUId: layer.folderUId }),
          //TODO: Why are we recalc summary when hiding or showing a layer?
          //copActions.calculateWorkflowStatusSummary({}),
          copActions.setLayerLoadingOffToStore({ layerUId: layer.uId })
        ];
      })
    )
  );

  hideLayers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.hideLayers),
      switchMap(action => {
        var foldersToProcess = [];

        action.layers.forEach(l => {
          foldersToProcess.push(l.folderUId);
        });
        return [
          copActions.setLayerVisibilitiesToStore({ layers: action.layers, showLayer: false }),
          copActions.updateFoldersPartialVisibilityState({ folderUIds: foldersToProcess })
          //TODO: Why are we recalc summary when hide layer
          //copActions.calculateWorkflowStatusSummary({})
        ];
      })
    )
  );

  showLayers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.showLayers),
      switchMap(action => {
        var foldersToProcess = [];
        var thenWorkerActions = [];

        action.layers.forEach(l => {
          foldersToProcess.push(l.folderUId);
        });

        return [
          copActions.setLayerVisibilitiesToStore({ layers: action.layers, showLayer: true }),
          copActions.updateFoldersPartialVisibilityState({ folderUIds: foldersToProcess })
        ];
      })
    )
  );

  showWeatherDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.showWeatherDocuments),
      withLatestFrom(this.store$.select(copSelectors.selectHiddenDocumentsByLayer)),

      switchMap(([action, hiddenDocs]) => {
        let changed = false;
        const newValue = produce(hiddenDocs, draft => {
          if (!!draft[action.layerUId]) {
            action.uids.forEach(d => {
              const index = draft[action.layerUId].indexOf(d);
              if (index >= 0) {
                draft[action.layerUId].splice(index, 1);
                changed = true;
              }
            });
          }
        });
        if (changed) {
          return [copActions.setHiddenDocumentsByLayerToStore({ hiddenDocumentsByLayer: newValue })];
        } else {
          return [];
        }
      })
    )
  );

  hideWeatherDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.hideWeatherDocuments),
      withLatestFrom(this.store$.select(copSelectors.selectHiddenDocumentsByLayer)),
      switchMap(([action, hiddenDocs]) => {
        let changed = false;
        const newValue = produce(hiddenDocs, draft => {
          if (!!draft[action.layerUId]) {
            action.uids.forEach(d => {
              if (!draft[action.layerUId].includes(d)) {
                draft[action.layerUId].push(d);
                changed = true;
              }
            });
          } else {
            draft[action.layerUId] = action.uids;
            changed = true;
          }
        });
        if (changed) {
          return [copActions.setHiddenDocumentsByLayerToStore({ hiddenDocumentsByLayer: newValue })];
        } else {
          return [];
        }
      })
    )
  );

  updateFolderPartialVisibilityState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.updateFolderPartialVisibilityState),
      withLatestFrom(
        this.store$.select(copSelectors.selectFolderVisibility),
        this.store$.select(copSelectors.selectLayerVisibility),
        this.store$.select(copSelectors.selectSelectedMapState)
      ),
      switchMap(([action, folderVisibility, layerVisibility, currentMapState]) => {
        const { folderUId } = action;
        const folder = currentMapState.foldersSet[folderUId];

        const newFolderVisibilitySettings = { ...folderVisibility };

        let folderToProcess: Folder = folder;
        while (!!folderToProcess) {
          let layerFolderVisibilityCounts = (folderToProcess.layers || []).reduce(
            (prevCounts, layer) => {
              const layerIsVisible = layerVisibility[layer.uId] !== false;
              const newCount = { ...prevCounts };
              if (layerIsVisible) {
                newCount.visible++;
              } else {
                newCount.hidden++;
              }
              return newCount;
            },
            { visible: 0, hidden: 0, partial: 0 }
          );

          layerFolderVisibilityCounts = (folderToProcess.folders || []).reduce((prevCounts, childFolder) => {
            const folderVis = newFolderVisibilitySettings[childFolder.uId] || FolderVisibility.visible;
            const newCount = { ...prevCounts };

            if (folderVis === FolderVisibility.hidden) {
              newCount.hidden++;
            } else if (folderVis === FolderVisibility.visible) {
              newCount.visible++;
            } else if (folderVis === FolderVisibility.partial) {
              newCount.partial++;
            }

            return newCount;
          }, layerFolderVisibilityCounts);

          let folderVisibilityValue = FolderVisibility.notSet;
          if (
            layerFolderVisibilityCounts.partial > 0 ||
            (layerFolderVisibilityCounts.hidden > 0 && layerFolderVisibilityCounts.visible > 0)
          ) {
            folderVisibilityValue = FolderVisibility.partial;
          } else if (layerFolderVisibilityCounts.visible > 0) {
            folderVisibilityValue = FolderVisibility.visible;
          } else if (layerFolderVisibilityCounts.hidden > 0) {
            folderVisibilityValue = FolderVisibility.hidden;
          }

          newFolderVisibilitySettings[folderToProcess.uId] = folderVisibilityValue;

          // get the next parent to process
          folderToProcess = !!folderToProcess.parentUId ? currentMapState.foldersSet[folderToProcess.parentUId] : null;
        }

        return [copActions.updateFolderVisibilityToStore({ folderVisibility: newFolderVisibilitySettings })];
      })
    )
  );

  updateFoldersPartialVisibilityState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.updateFoldersPartialVisibilityState),
      withLatestFrom(
        this.store$.select(copSelectors.selectFolderVisibility),
        this.store$.select(copSelectors.selectLayerVisibility),
        this.store$.select(copSelectors.selectSelectedMapState)
      ),
      switchMap(([action, folderVisibility, layerVisibility, currentMapState]) => {
        const newFolderVisibilitySettings = { ...folderVisibility };

        action.folderUIds.forEach(folderUId => {
          const folder = currentMapState.foldersSet[folderUId];

          let folderToProcess: Folder = folder;
          while (!!folderToProcess) {
            let layerFolderVisibilityCounts = (folderToProcess.layers || []).reduce(
              (prevCounts, layer) => {
                const layerIsVisible = layerVisibility[layer.uId] !== false;
                const newCount = { ...prevCounts };
                if (layerIsVisible) {
                  newCount.visible++;
                } else {
                  newCount.hidden++;
                }
                return newCount;
              },
              { visible: 0, hidden: 0, partial: 0 }
            );

            layerFolderVisibilityCounts = (folderToProcess.folders || []).reduce((prevCounts, childFolder) => {
              const folderVis = newFolderVisibilitySettings[childFolder.uId] || FolderVisibility.visible;
              const newCount = { ...prevCounts };

              if (folderVis === FolderVisibility.hidden) {
                newCount.hidden++;
              } else if (folderVis === FolderVisibility.visible) {
                newCount.visible++;
              } else if (folderVis === FolderVisibility.partial) {
                newCount.partial++;
              }

              return newCount;
            }, layerFolderVisibilityCounts);

            let folderVisibilityValue = FolderVisibility.notSet;
            if (
              layerFolderVisibilityCounts.partial > 0 ||
              (layerFolderVisibilityCounts.hidden > 0 && layerFolderVisibilityCounts.visible > 0)
            ) {
              folderVisibilityValue = FolderVisibility.partial;
            } else if (layerFolderVisibilityCounts.visible > 0) {
              folderVisibilityValue = FolderVisibility.visible;
            } else if (layerFolderVisibilityCounts.hidden > 0) {
              folderVisibilityValue = FolderVisibility.hidden;
            }

            newFolderVisibilitySettings[folderToProcess.uId] = folderVisibilityValue;

            // get the next parent to process
            folderToProcess = !!folderToProcess.parentUId ? currentMapState.foldersSet[folderToProcess.parentUId] : null;
          }
        });

        return [copActions.updateFolderVisibilityToStore({ folderVisibility: newFolderVisibilitySettings })];
      })
    )
  );

  updateSelectedLayerQuickfilterSelections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.updateSelectedLayerQuickfilterSelections),
      withLatestFrom(this.store$.select(copSelectors.selectLayerQuickFilterSelections)),
      switchMap(([action, currentQuickFilterSelections]) => {
        const { layerUId, quickFilterSelections } = action;

        const newQuickFilterSelections: { [layerUId: string]: { [filterUId: string]: { selectedValues: any[] } } } = {
          ...currentQuickFilterSelections
        };
        if (!quickFilterSelections) {
          delete newQuickFilterSelections[layerUId];
        } else {
          newQuickFilterSelections[layerUId] = {};
          Object.keys(quickFilterSelections).forEach(k => {
            if (!newQuickFilterSelections[layerUId][k]) {
              newQuickFilterSelections[layerUId][k] = { selectedValues: [] };
            }
            const optionKeys = Object.keys(quickFilterSelections[k].options);
            const selectedValues: (string | number)[] = [];
            Object.keys(quickFilterSelections[k].options).forEach(optKey => {
              quickFilterSelections[k].options[optKey].forEach(sel => selectedValues.push(sel.value));
            });
            newQuickFilterSelections[layerUId][k].selectedValues = selectedValues;
          });
        }

        return [
          copActions.setLayerQuickFilterSelectionsToStore({ layerQuickFilterSelections: newQuickFilterSelections }),
          copActions.loadDocumentsForMapLayer({ layerUId: layerUId })
        ];
      })
    )
  );

  hideShowFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.hideShowFolder),
      withLatestFrom(this.store$.select(copSelectors.selectFolderVisibility), this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, folderVisibility, selectedMapState]) => {
        const { folder } = action;

        const existingFolderVisibility = folderVisibility[folder.uId] || FolderVisibility.visible;
        const isPartial = existingFolderVisibility === FolderVisibility.partial;
        const isVisible = existingFolderVisibility === FolderVisibility.visible;
        let newFolderVisibility: number;
        if (isVisible || isPartial) {
          newFolderVisibility = FolderVisibility.hidden;
        } else {
          newFolderVisibility = FolderVisibility.visible;
        }

        const containedLayers = this.getAllLayersInFolder(folder);
        const containedFolders = this.getAllFoldersInFolder(folder);

        if (newFolderVisibility !== FolderVisibility.visible) {
          return [
            copActions.setFolderVisibilityToHiddenToStore({ folder, containedFolders, containedLayers }),
            copActions.updateFolderPartialVisibilityState({ folderUId: folder.uId })
          ];
        } else {
          return [
            copActions.setFolderVisibiltyToNotHiddenToStore({ folder, containedFolders, containedLayers }),
            copActions.updateFolderPartialVisibilityState({ folderUId: folder.uId })
          ];
        }
      })
    )
  );

  moveFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.moveFolder),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, selectedMapState]) => {
        const { sourceFolderUId, destinationUId, destinationType, moveAction } = action;

        const updatedMapState = produce(selectedMapState, draft => {
          const folderToMove = this.getFolderInMap(draft.map.folders, sourceFolderUId);
          const finalMoveAction = destinationType === 'layer' && moveAction === 'inside' ? 'after' : moveAction;

          // remove the layer from the current parent
          const currentParentFolder = this.getFolderInMap(draft.map.folders, folderToMove.parentUId);
          let idx = currentParentFolder.folders.findIndex(f => f.uId === folderToMove.uId);
          currentParentFolder.folders.splice(idx, 1);

          let targetFolderUId: string;
          if (destinationType === 'folder' && finalMoveAction === 'inside') {
            targetFolderUId = destinationUId;
          } else {
            targetFolderUId =
              destinationType === 'folder'
                ? this.getFolderInMap(draft.map.folders, destinationUId).parentUId
                : this.getLayerInMap(draft.map.folders, destinationUId).folderUId;
          }

          // add layer to target folder
          let destFolder = this.getFolderInMap(draft.map.folders, targetFolderUId);
          folderToMove.parentUId = destFolder.uId;

          const destFolderContentsBySortOrder: { uId: string; sortOrder: number; item: Folder | Layer }[] = destFolder.folders
            .map(f => {
              return { uId: f.uId, sortOrder: f.sortOrder, item: f } as { uId: string; sortOrder: number; item: Folder | Layer };
            })
            .concat(
              destFolder.layers.map(l => {
                return { uId: l.uId, sortOrder: l.sortOrder, item: l };
              })
            );
          destFolderContentsBySortOrder.sort((a, b) => a.sortOrder - b.sortOrder);

          // I am a Folder, add me to the folder, then add to sort order array
          // 1. If moving inside a Folder
          //      then move to the last position in that folder
          // 2. if moving before or after a Folder or Layer
          //      then insert into that ordered list

          destFolder.folders.push(folderToMove);
          if (finalMoveAction === 'inside') {
            destFolderContentsBySortOrder.push({ uId: folderToMove.uId, sortOrder: 0, item: folderToMove });
          } else {
            idx = destFolderContentsBySortOrder.findIndex(i => i.uId === destinationUId);
            idx = moveAction === 'after' ? idx + 1 : idx;
            destFolderContentsBySortOrder.splice(idx, 0, ...[{ uId: folderToMove.uId, sortOrder: 0, item: folderToMove }]);
          }
          for (let i = 0; i < destFolderContentsBySortOrder.length; i++) {
            destFolderContentsBySortOrder[i].item.sortOrder = i + 1;
          }

          this.mapStateService.updateMapStateSetsFromMap(draft);
        });

        return [copActions.setDraftMapToStore({ mapState: updatedMapState })];
      })
    )
  );

  moveLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.moveLayer),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, selectedMapState]) => {
        const { sourceLayerUId, destinationUId, destinationType, moveAction } = action;

        const updatedMapState = produce(selectedMapState, draft => {
          const layerToMove = this.getLayerInMap(draft.map.folders, sourceLayerUId);
          const finalMoveAction = destinationType === 'layer' && moveAction === 'inside' ? 'after' : moveAction;

          // remove the layer from the current parent
          const currentParentFolder = this.getFolderInMap(draft.map.folders, layerToMove.folderUId);
          let idx = currentParentFolder.layers.findIndex(f => f.uId === layerToMove.uId);
          currentParentFolder.layers.splice(idx, 1);

          let targetFolderUId: string;
          if (destinationType === 'folder' && finalMoveAction === 'inside') {
            targetFolderUId = destinationUId;
          } else {
            targetFolderUId =
              destinationType === 'folder'
                ? this.getFolderInMap(draft.map.folders, destinationUId).parentUId
                : this.getLayerInMap(draft.map.folders, destinationUId).folderUId;
          }

          // add layer to target folder
          let destFolder = this.getFolderInMap(draft.map.folders, targetFolderUId);
          layerToMove.folderUId = destFolder.uId;

          const destFolderContentsBySortOrder: { uId: string; sortOrder: number; item: Folder | Layer }[] = destFolder.folders
            .map(f => {
              return { uId: f.uId, sortOrder: f.sortOrder, item: f } as { uId: string; sortOrder: number; item: Folder | Layer };
            })
            .concat(
              destFolder.layers.map(l => {
                return { uId: l.uId, sortOrder: l.sortOrder, item: l };
              })
            );
          destFolderContentsBySortOrder.sort((a, b) => a.sortOrder - b.sortOrder);

          // I am a Layer, add me to the folder, then add to sort order array
          // 1. If moving inside a Folder
          //      then move to the last position in that folder
          // 2. if moving before or after a Folder or Layer
          //      then insert into that ordered list

          destFolder.layers.push(layerToMove);
          if (finalMoveAction === 'inside') {
            destFolderContentsBySortOrder.push({ uId: layerToMove.uId, sortOrder: 0, item: layerToMove });
          } else {
            idx = destFolderContentsBySortOrder.findIndex(i => i.uId === destinationUId);
            idx = moveAction === 'after' ? idx + 1 : idx;
            destFolderContentsBySortOrder.splice(idx, 0, ...[{ uId: layerToMove.uId, sortOrder: 0, item: layerToMove }]);
          }
          for (let i = 0; i < destFolderContentsBySortOrder.length; i++) {
            destFolderContentsBySortOrder[i].item.sortOrder = i + 1;
          }

          this.mapStateService.updateMapStateSetsFromMap(draft);
        });

        return [copActions.setDraftMapToStore({ mapState: updatedMapState })];
      })
    )
  );

  moveSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.moveSummary),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState), this.store$.select(copSelectors.selectWorkflowStatusSummary)),
      switchMap(([action, selectedMapState, ws]) => {
        const { sourceSummaryUId, destinationUId, destinationType, moveAction } = action;
        const sectionUId = destinationUId.substring(0, destinationUId.lastIndexOf('-'));
        const sourceName = sourceSummaryUId.substring(sourceSummaryUId.lastIndexOf('-') + 1);
        const targetName = destinationUId.substring(destinationUId.lastIndexOf('-') + 1);

        const updatedMapState = produce(selectedMapState, draft => {
          const section = this.getFolderInMap(draft.map.folders, sectionUId);
          if (!section) {
            return;
          }

          const configuration = section.configuration ? (JSON.parse(section.configuration) as FolderConfiguration) : null;

          const workflowSummary = ws.find(s => s.sectionUId === sectionUId && s.type === 'section');

          const newOrder = configuration?.workflowStepSortOrder || [];

          // Add any missing values to user settings
          workflowSummary.summaryByStatus.forEach(s => {
            if (!newOrder.find(n => n === s.name)) {
              newOrder.push(s.name);
            }
          });

          // Remove Source Name
          const oldIndex = newOrder.findIndex(n => n === sourceName);
          if (oldIndex >= 0) {
            newOrder.splice(oldIndex, 1);
          }

          // Add after target
          const newIndex = newOrder.findIndex(n => n === targetName);
          if (newIndex >= 0) {
            newOrder.splice(newIndex + 1, 0, sourceName);
          }
          configuration.workflowStepSortOrder = newOrder;

          // Save new configuration to section draft
          section.configuration = JSON.stringify(configuration);
        });

        return [copActions.setDraftMapToStore({ mapState: updatedMapState })];
      })
    )
  );

  // Workflow summary
  calculateWorkflowStatusSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.calculateWorkflowStatusSummary),
      withLatestFrom(
        this.store$.select(copSelectors.selectDatasetsSet),
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectWorkflowStatusVisibilityByLayerTypeAndName),
        this.store$.select(copSelectors.selectWorkflowStatusSummary)
      ),
      switchMap(([{ summaryItems }, datasetsSet, mapState, workflowVisibility, currentStatus]) => {
        const calculatedWorkflowSummaryArray = (summaryItems ?? currentStatus).map(currentStatus => {
          const statusModel: MapLayerSummaryByStatus[] = [];

          const workflowStepVisibilityKey =
            currentStatus.type === 'section' ? currentStatus.sectionUId : LayerTypes[currentStatus.layerType];
          const workflowVisibilityForSectionOrLayerType = workflowVisibility[workflowStepVisibilityKey] || [];
          const foldersInSection = [currentStatus.sectionUId] as string[];
          const workflowStatusLayers =
            currentStatus.type === 'section'
              ? FolderUtils.findLayers(mapState.map?.folders, (l, f) => {
                  if (!foldersInSection.find(v => v === f.uId) && foldersInSection.find(v => v === f.parentUId)) {
                    foldersInSection.push(f.uId);
                  }
                  return !!foldersInSection.find(v => v === f.uId) && l.isActive;
                })
              : Object.values(mapState.layersSet).filter(l => l.type === currentStatus.layerType && l.isActive);

          workflowStatusLayers
            ?.map(l => [l, datasetsSet[l.datasetUId], mapState.layerStates[l.uId]])
            .forEach(ar => {
              const layer = ar[0] as Layer;
              const dataset = ar[1] as Dataset;
              const layerState = ar[2] as LayerState;
              const workflowsteps = dataset?.workflow?.workflowSteps || [];

              // section-layer level workflow visibility settings
              const workflowVisibilityForThisLayer = workflowVisibility[`${currentStatus?.sectionUId}-${layer.uId}`] || [];

              // populate docs by status
              workflowsteps.forEach(s => {
                let statusForThisStepName = statusModel.find(sm => sm.name === s.statusName);
                if (!statusForThisStepName) {
                  statusForThisStepName = {
                    workflowStepUIds: {},
                    name: s.statusName,
                    count: 0,
                    colorCode: s.statusHtmlColorCode,
                    layerDocs: [],
                    hiddenByUser: !workflowVisibilityForSectionOrLayerType
                      ? false
                      : !!workflowVisibilityForSectionOrLayerType.find(n => n == s.statusName),
                    sortOrder: s.sortOrder
                  };
                  statusModel.push(statusForThisStepName);
                }
                statusForThisStepName.workflowStepUIds[layer.uId] = s.uId;
                if (
                  !statusForThisStepName.layerDocs.find(ws => ws.workflowStatusUId === s.workflowStatusUId && ws.layerUId === layer.uId)
                ) {
                  // Add all workflow step Ids for later filtering of display numbers
                  statusForThisStepName.layerDocs.push({
                    documentCount: 0,
                    isVisible: layerState.isVisible,
                    layerUId: layer.uId,
                    stepColorCode: s.statusHtmlColorCode,
                    isWorkflowStepHidden: !!workflowVisibilityForThisLayer.find(n => n == s.statusName),
                    workflowStatusUId: s.workflowStatusUId
                  });
                }
              });

              Object.values(layerState?.documents)?.forEach(doc => {
                const ws = workflowsteps.find(w => w.uId === doc[SystemFieldNames.workflowStepUId]);
                if (!ws) {
                  return []; // If a document has a workflow step that is not in the dataset definition, ignore it
                }
                let statusForThisStep = statusModel
                  .find(sm => sm.name === ws.statusName)
                  .layerDocs.find(ld => ld.workflowStatusUId === ws.workflowStatusUId && ld.layerUId === layer.uId);
                statusForThisStep.documentCount++;
              });
            });

          if (workflowStatusLayers) {
            statusModel.forEach(stats => {
              stats.count = stats.layerDocs.map(docs => docs.documentCount).reduce((pv, cv) => pv + cv, 0);
            });
          }

          const statusCounts: MapLayerSummaryStatusCounts = {
            inProgress: 0,
            pending: 0
          };

          if (statusModel.length > 0) {
            // If a stepsInSummary configuration is available, look for steps that are configured to be in this status
            const pendingStatusList = currentStatus.stepsInSummary
              ? statusModel.map(m =>
                  m.layerDocs.filter(ld => currentStatus.stepsInSummary.find(s => ld.workflowStatusUId === s.uId)?.option === 'pending')
                )
              : statusModel.slice(0, 1).map(m => m.layerDocs);

            const inProgressStatusList = currentStatus.stepsInSummary
              ? statusModel.map(m =>
                  m.layerDocs.filter(ld => currentStatus.stepsInSummary.find(s => ld.workflowStatusUId === s.uId)?.option === 'in-progress')
                )
              : statusModel.slice(1, statusModel.length - 1).map(m => m.layerDocs);

            statusCounts.inProgress = inProgressStatusList.reduce((pv, ldArr) => pv + ldArr.reduce((v, el) => v + el.documentCount, 0), 0);
            statusCounts.pending = pendingStatusList.reduce((pv, ldArr) => pv + ldArr.reduce((v, el) => v + el.documentCount, 0), 0);
          }

          return {
            ...currentStatus,
            statusCounts,
            summaryByStatus: statusModel.sort((sm1, sm2) => {
              return sm1.sortOrder > sm2.sortOrder ? 1 : -1;
            })
          } as MapLayerSummary;
        });

        return [copActions.setWorkflowStatusSummary({ summary: calculatedWorkflowSummaryArray })];
      })
    )
  );

  calculateMapTileLayers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.calculateMapTileLayers),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState), this.store$.select(lsdSelectors.selectLayerOpacity)),
      switchMap(([action, mapState, layerOpacitySettings]) => {
        const enableTileServer = Environment.enableTileServer;

        const layersToTile = Object.values(mapState.layersSet)
          .filter(layer => {
            layer.type === LayerTypes.Tile || (layer.type == LayerTypes.WeatherImagery && !layer.datasetUId);
          })
          .filter(l => mapState.layerStates[l.uId]?.isVisible === true);

        if (!layersToTile || layersToTile.length === 0) {
          return [copActions.setMapTileLayersToStore({ tileLayers: [] })];
        }

        const ungroupedTileLayers = layersToTile.map(l => {
          let urlTemplate = l.foreignDatasetDefinition.urlTemplate;
          if (l.foreignDatasetDefinition.useSystemClientId) {
            // Useful for system defined keys
            const ids = Environment[l.foreignDatasetDefinition.useSystemClientId];
            if (ids) {
              urlTemplate = urlTemplate.replace('{client_id}', ids.client_id).replace('{client_key}', ids.client_key);
            }
          }

          let opacity = layerOpacitySettings[l.uId] >= 0 ? layerOpacitySettings[l.uId] : l.foreignDatasetDefinition.opacity;
          if (l.displayOptions.shapeOpacity > 0) {
            opacity = l.displayOptions.shapeOpacity;
          }

          let isAnimated = false;
          if (l.type == LayerTypes.WeatherImagery && !l.datasetUId) {
            const layerFolder = mapState.foldersSet[l.folderUId];
            if (!!layerFolder?.configuration) {
              const folderConfig = JSON.parse(layerFolder.configuration) as FolderConfiguration;
              if (!isNaN(folderConfig?.shapeOpacity)) {
                opacity = folderConfig.shapeOpacity;
              }
            }

            isAnimated = mapState.layerStates[l.uId]?.isAnimated;
          }

          return {
            name: l.uId,
            urlTemplate,
            templateGroup: l.foreignDatasetDefinition.templateGroup,
            options: l.foreignDatasetDefinition.options,
            opacity,
            refreshInterval: l.foreignDatasetDefinition.refreshInterval,
            tileSize: l.foreignDatasetDefinition.tileSize,
            isAnimated
          } as ImageTileLayer;
        });

        // Group Tile Layers by templateGroup
        const tileLayers = [];
        ungroupedTileLayers.forEach(tl => {
          const fl = tileLayers.find(l => l.templateGroup === tl.templateGroup);
          if (!fl) {
            tileLayers.push(tl);
            return;
          }

          fl.options = fl.options.map(o => {
            return {
              ...o,
              enabled: !!o?.enabled || tl.options.find(oo => oo.value === o.value)?.enabled
            };
          });

          // Add any options that tl has that the fl doesn't
          fl.options = [...fl.options, ...tl.options.filter(o => !fl.options.find(fo => fo.value === o.value))];
        });

        return [copActions.setMapTileLayersToStore({ tileLayers })];
      })
    )
  );

  removeFolderInMap = (parentFolder: Folder, folder: Folder): boolean => {
    const index = parentFolder?.folders?.findIndex(f => f.uId === folder.uId);
    if (index >= 0) {
      parentFolder.folders.splice(index, 1);
      return true;
    }

    for (const childFolder of parentFolder.folders) {
      if (this.removeFolderInMap(childFolder, folder)) {
        return true;
      }
    }

    return false;
  };

  getAllLayersInFolder = (folder: Folder, layersFound?: Layer[]): Layer[] => {
    const layers = layersFound || ([] as Layer[]);

    for (const childLayer of folder.layers) {
      layers.push(childLayer);
    }

    for (const childFolder of folder.folders) {
      this.getAllLayersInFolder(childFolder, layers);
    }

    return layers;
  };

  getAllFoldersInFolder = (folder: Folder, foldersFound?: Folder[]): Folder[] => {
    const folders = foldersFound || ([] as Folder[]);

    for (const childFolder of folder.folders) {
      folders.push(childFolder);

      this.getAllFoldersInFolder(childFolder, folders);
    }

    return folders;
  };

  removeLayerInMap = (parentFolder: Folder, layer: Layer): boolean => {
    const index = parentFolder?.layers?.findIndex(l => l.uId === layer.uId);
    if (index >= 0) {
      parentFolder.layers.splice(index, 1);
      return true;
    }

    for (const childFolder of parentFolder.folders) {
      if (this.removeLayerInMap(childFolder, layer)) {
        return true;
      }
    }

    return false;
  };

  tryAddLayerToFolder = (parentFolderUId: string, folder: Folder, layer: Layer) => {
    if (folder.uId === parentFolderUId) {
      folder.layers.push(layer);
      return;
    }

    for (const childFolder of folder.folders) {
      this.tryAddLayerToFolder(parentFolderUId, childFolder, layer);
    }
  };

  tryAddFolderToFolder = (parentFolderUId: string, parentFolder: Folder, folder: Folder) => {
    if (parentFolder.uId === parentFolderUId) {
      parentFolder.folders.push(folder);
      return;
    }

    for (const childFolder of parentFolder.folders) {
      this.tryAddFolderToFolder(parentFolderUId, childFolder, folder);
    }
  };

  updateFolderInMap = (parentFolder: Folder, folder: Folder): boolean => {
    const index = parentFolder?.folders?.findIndex(f => f.uId === folder.uId);
    if (index >= 0) {
      parentFolder.folders.splice(index, 1, ...[folder]);
      return true;
    }

    for (const childFolder of parentFolder.folders) {
      if (this.updateFolderInMap(childFolder, folder)) {
        return true;
      }
    }

    return false;
  };

  getFolderInMap = (folders: Folder[], folderUId: string): Folder => {
    if (!folders || !folderUId) {
      return null;
    }

    let folder = folders.find(f => f.uId === folderUId);
    if (!!folder) {
      return folder;
    }

    for (const childFolder of folders) {
      folder = this.getFolderInMap(childFolder.folders, folderUId);
      if (!!folder) {
        return folder;
      }
    }

    return null;
  };

  getLayerInMap = (folders: Folder[], layerUId: string): Layer => {
    if (!folders || !layerUId) {
      return null;
    }

    for (const folder of folders) {
      let layer = folder.layers.find(l => l.uId === layerUId);
      if (!!layer) {
        return layer;
      }

      layer = this.getLayerInMap(folder.folders, layerUId);
      if (!!layer) {
        return layer;
      }
    }

    return null;
  };

  updateLayerInMap = (parentFolder: Folder, layer: Layer): boolean => {
    const index = parentFolder?.layers?.findIndex(l => l.uId === layer.uId);
    if (index >= 0) {
      parentFolder.layers.splice(index, 1, ...[layer]);
      return true;
    }

    for (const childFolder of parentFolder.folders) {
      if (this.updateLayerInMap(childFolder, layer)) {
        return true;
      }
    }

    return false;
  };
}
